import { getLocaleString } from '@assets/i18n/config';
import type { Breach, BreachType, DriftAccount } from '@interfaces/driftQueue';
import PublicIcon from '@mui/icons-material/Public';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Tooltip, useTheme } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import type { ColumnDef } from '@ovComponents/2-component/dataTable';
import { AssetProgressBar } from '@ovComponents/3-pattern/assetProgressBar/assetProgressBar';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Returns the breach with the lowest `severity` integer (highest severity).
 * Returns `null` when the array is empty.
 */
export function getHighestSeverityBreach(breaches: Breach[]): Breach | null {
  if (breaches.length === 0) return null;
  return breaches.reduce((highest, current) => {
    return current.severity < highest.severity ? current : highest;
  });
}

/**
 * Counts unique breach reasons (deduplicates exact `reason` matches).
 */
export function getDistinctReasonCount(breaches: Breach[]): number {
  return new Set(breaches.map((b) => b.reason)).size;
}

/**
 * Maps a `BreachType` to sys.color tokens.
 * Centralised here so changing the token mapping is a one-line edit.
 */
export type TokenForBreachFn = (type: BreachType) => string;

export function useDriftQueueColumns(): ColumnDef<DriftAccount, unknown>[] {
  const { t } = useTranslation(['driftQueue']);
  const theme = useTheme();
  const { sys } = useThemeTokens();
  const { activeCurrency } = useContext(UserContext);
  const currencyCode = activeCurrency || 'CAD';

  /** Background color for the AssetProgressBar segments. */
  const tokenForBreach: TokenForBreachFn = (type) =>
    type === 'single'
      ? (sys.color.warning ?? theme.palette.warning.main)
      : (sys.color.negative ?? theme.palette.error.main);

  /** Colors for the breach reason pill chip. */
  const chipColors = (type: BreachType) =>
    type === 'single'
      ? { bg: sys.color.warningVariant, text: sys.color.onWarningVariant }
      : { bg: sys.color.negativeVariant, text: sys.color.onNegativeVariant };

  const columns: ColumnDef<DriftAccount, unknown>[] = [
    {
      id: 'portfolio',
      accessorFn: (row) => row.portfolioName,
      header: t('driftQueue:table.portfolio'),
      enableSorting: true,
      cell: ({ row }) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant='bodySmall' weight='bold'>
              {row.original.portfolioName}
            </Typography>
            {row.original.isStale && (
              <Tooltip
                title={t('driftQueue:table.staleTooltip', {
                  date: row.original.asOfDate,
                })}
              >
                <ReportProblemIcon
                  aria-label={String(t('driftQueue:legend.stale'))}
                  sx={{ fontSize: 14, color: theme.palette.warning.dark }}
                />
              </Tooltip>
            )}
            {row.original.hasInternational && (
              <Tooltip title={t('driftQueue:legend.international')}>
                <PublicIcon
                  aria-label={String(t('driftQueue:legend.international'))}
                  sx={{ fontSize: 14, color: sys.color.onSurfaceVariant }}
                />
              </Tooltip>
            )}
          </Box>
          <Typography variant='labelSmall' colorVariant='variant'>
            {row.original.accountNumber}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'household',
      accessorKey: 'householdName',
      header: t('driftQueue:table.household'),
      enableSorting: false,
    },
    {
      id: 'accountType',
      accessorKey: 'accountType',
      header: t('driftQueue:table.accountType'),
      enableSorting: false,
      cell: ({ getValue }) => (
        <Box
          sx={{
            display: 'inline-flex',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            backgroundColor: sys.color.primaryVariant,
            // Keep the pill on a single line — long values (e.g. "Non-Registered")
            // otherwise wrap to 2 lines, making that row taller than its
            // neighbours. Because the active filter/sort changes which values are
            // visible, the inconsistent wrapping read as "dense breaks on click".
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            variant='labelSmall'
            sx={{ color: sys.color.onPrimaryVariant, whiteSpace: 'nowrap' }}
          >
            {getValue<string>()}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'reason',
      accessorFn: (row) => getHighestSeverityBreach(row.breaches)?.reason ?? '',
      header: t('driftQueue:table.reason'),
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const topBreach = getHighestSeverityBreach(row.original.breaches);
        if (!topBreach) return null;
        const distinctCount = getDistinctReasonCount(row.original.breaches);
        const { bg, text } = chipColors(row.original.breachType);

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {/* Solid-color reason pill */}
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.3,
                borderRadius: 10,
                backgroundColor: bg,
              }}
            >
              <ReportProblemIcon sx={{ fontSize: 13, color: text }} />
              <Typography
                variant='labelSmall'
                sx={{ color: text, fontWeight: 600 }}
              >
                {topBreach.reason}
              </Typography>
            </Box>

            {/* Extra alert count badge */}
            {distinctCount > 1 && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: bg,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{ color: text, fontWeight: 700 }}
                >
                  {distinctCount}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      id: 'custodian',
      accessorKey: 'custodian',
      header: t('driftQueue:table.custodian'),
      enableSorting: true,
    },
    {
      id: 'portfolioValue',
      accessorKey: 'portfolioValue',
      header: t('driftQueue:table.value'),
      enableSorting: true,
      meta: { dataTable: { align: 'right', numeric: true } },
      cell: ({ getValue }) =>
        new Intl.NumberFormat(getLocaleString(), {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(getValue<number>()),
    },
    {
      id: 'cashAndPosition',
      accessorFn: (row) => row.cashPositionPercentage,
      header: t('driftQueue:table.cashAndPosition'),
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const barColor = tokenForBreach(row.original.breachType);
        return (
          <Box sx={{ minWidth: 110 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.25,
              }}
            >
              <Typography variant='labelSmall' colorVariant='variant'>
                {t('driftQueue:table.cashShort')}
              </Typography>
              <Typography
                variant='labelSmall'
                sx={{ color: barColor, fontWeight: 700 }}
              >
                +{row.original.cashPositionPercentage.toFixed(1)}%
              </Typography>
            </Box>
            <AssetProgressBar
              data={[
                {
                  key: 'cash',
                  percentage: row.original.cashPositionPercentage,
                  colorCode: barColor,
                },
              ]}
              sx={{ mb: 0.5 }}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mb: 0.25,
              }}
            >
              <Typography variant='labelSmall' colorVariant='variant'>
                {t('driftQueue:table.positionShort')}
              </Typography>
              <Typography
                variant='labelSmall'
                sx={{ color: barColor, fontWeight: 700 }}
              >
                {row.original.positionDriftAvailable === false
                  ? '—'
                  : `+${row.original.assetPositionPercentage.toFixed(1)}%`}
              </Typography>
            </Box>
            {row.original.positionDriftAvailable === false ? (
              <Typography variant='labelSmall' colorVariant='variant'>
                {t('driftQueue:table.positionUnavailable')}
              </Typography>
            ) : (
              <AssetProgressBar
                data={[
                  {
                    key: 'position',
                    percentage: row.original.assetPositionPercentage,
                    colorCode: barColor,
                  },
                ]}
              />
            )}
          </Box>
        );
      },
    },
  ];

  return columns;
}
